import React, { useCallback } from 'react';
import { EdgeProps, getSmoothStepPath } from 'reactflow';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { actionsSelector } from '~/components/page/Automation/v2/state/actions';
import AnimatedPath from '../AnimatedPath';
import AddBlockButton from '../AddBlockButton';
import { getActionTemplate } from '../../../../constants/actionTemplates';
import useBuilderContext from '../../../../hooks/useBuilderContext';
import onUpdateActions from '../../utils/onUpdateActions';
import { FlowAction } from '~/graphql/types';
import { ClientFlowAction } from '~/graphql/types.client';
import { shouldDisable } from '~/components/page/Automation/v2/state/interactions';
import { EDGE_BORDER_RADIUS } from '../BaseEdge';
import { MARKER_END_DISABLED } from '../../../MarkerDefinitions';
import usePasteAction from '~/components/page/Automation/v2/components/Builder/hooks/usePasteAction';
import PasteContainer from '../PasteContainer';
import type { HandlerId } from '../../../nodeTypes/components/IfElseCard/types';
import { PathType } from '../../../nodeTypes/components/IfElseCard/types';
import dialogs from '~/components/page/Automation/v2/state/dialogs';

export type EdgeData = {
  action: ClientFlowAction;
  parentNode: ClientFlowAction;
  id: string;
  parentId: string;
};

export type Props = EdgeProps<EdgeData> & {};

const DropEdge: React.FCC<Props> = React.memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    data,
    source,
    target,
    markerEnd,
    sourceHandleId,
  }) => {
    const { flowBlueprintId, accountId } = useBuilderContext();

    const edgePath = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: EDGE_BORDER_RADIUS,
    });

    const setActions = useSetRecoilState(
      actionsSelector({
        accountId,
        flowBlueprintId,
      }),
    );
    const setDialog = useSetRecoilState(dialogs);

    const onAddAction = useCallback(
      (actionType: ClientFlowAction['actionType']) => {
        const actionTemplate = getActionTemplate({
          flowBlueprintId,
          accountId,
          actionType,
        });

        if (actionTemplate === null || data == null) return;

        if (actionType === FlowAction.IfElse) {
          setDialog({
            type: 'dialogIfElsePathSelection',
            mode: 'insert',
            action: actionTemplate,
            onConfirm: pathType => {
              setDialog(null);
              if (pathType === null) return;
              setActions(actions =>
                onUpdateActions(actions, {
                  source,
                  target,
                  subject: actionTemplate,
                  isPasteTruePath: pathType === PathType.True,
                }),
              );
            },
            onCancel: () => setDialog(null),
          });
          return;
        }

        setActions(actions =>
          onUpdateActions(actions, {
            source,
            target,
            subject: actionTemplate,
          }),
        );
      },
      [accountId, data, flowBlueprintId, source, target, setActions, setDialog],
    );

    const { onInsertAction } = usePasteAction({
      source,
      target,
      sourceKey: sourceHandleId as HandlerId,
    });

    const disabled = useRecoilValue(shouldDisable);

    return (
      <>
        <AnimatedPath
          id={id}
          style={{
            ...style,
            strokeWidth: 2,
            pointerEvents: 'none',
            opacity: disabled ? 0.5 : 1,
            transition: 'opacity 0.5s ease-out',
          }}
          className="react-flow__edge-path"
          d={edgePath[0]}
          markerEnd={disabled ? MARKER_END_DISABLED : markerEnd}
        />
        <PasteContainer
          onInsertAction={onInsertAction}
          sourceId={source}
          sourceKey={sourceHandleId as HandlerId}
          targetId={target}
          sourceX={sourceX}
          targetX={targetX}
          sourceY={sourceY}
          targetY={targetY}
        />

        {!disabled && (
          <AddBlockButton
            id={id}
            targetX={targetX}
            targetY={targetY}
            onAddAction={onAddAction}
            parentActionType={data?.parentNode?.actionType}
          />
        )}
      </>
    );
  },
);

export default DropEdge;
